/**
 * Client-side activity logger.
 *
 * Every interesting thing the operator does (or the app does on their behalf)
 * lands in a single in-memory ring buffer with a structured shape. The
 * LogViewer overlay subscribes to it for a live timeline; export() dumps it
 * to a JSON file for bug reports.
 *
 * Logs are NOT sent to the Hub — they stay client-side. Hub-side audit logs
 * (e.g. `audit: command dispatched`) cover the server perspective; this
 * captures everything the operator did regardless of whether it reached the
 * Hub.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export type LogCategory =
  | 'action'   // operator clicked a button or executed a shortcut
  | 'command'  // outbound Command frame to the Hub
  | 'ws'       // inbound WebSocket message from the Hub
  | 'router'   // route navigation
  | 'mode'     // map mode switch
  | 'auth'     // login / logout / token rehydrate
  | 'select'   // asset selection
  | 'mission'  // waypoint add / move / delete
  | 'error';   // caught exception or boundary trip

export interface LogEntry {
  /** Monotonic id. */
  id: number;
  /** Epoch ms of the event. */
  ts: number;
  level: LogLevel;
  category: LogCategory;
  message: string;
  /** Anything structured worth keeping. */
  data?: Record<string, unknown>;
}

const RING_MAX = 1000;
const ring: LogEntry[] = [];
const listeners = new Set<(e: LogEntry) => void>();
let nextId = 1;

/** Toggle to mirror entries to the browser console. Useful in dev. */
let consoleMirror = (import.meta as { env?: { DEV?: boolean } }).env?.DEV ?? false;

function emit(
  level: LogLevel,
  category: LogCategory,
  message: string,
  data?: Record<string, unknown>,
) {
  const entry: LogEntry = {
    id: nextId++,
    ts: Date.now(),
    level,
    category,
    message,
    ...(data ? { data } : {}),
  };
  ring.push(entry);
  if (ring.length > RING_MAX) ring.shift();
  if (consoleMirror) {
    const fn =
      level === 'error' ? console.error : level === 'warn' ? console.warn : console.log;
    fn(`[${category}] ${message}`, data ?? '');
  }
  for (const l of listeners) l(entry);
}

/**
 * Public API. Category helpers exist so call sites read like one-liners
 * (`log.action('Takeoff clicked', { robotId, alt: 10 })`) rather than
 * needing to remember the level/category enums.
 */
export const log = {
  // Info-level helpers, one per category.
  action: (m: string, d?: Record<string, unknown>) => emit('info', 'action', m, d),
  command: (m: string, d?: Record<string, unknown>) => emit('info', 'command', m, d),
  ws: (m: string, d?: Record<string, unknown>) => emit('info', 'ws', m, d),
  router: (m: string, d?: Record<string, unknown>) => emit('info', 'router', m, d),
  mode: (m: string, d?: Record<string, unknown>) => emit('info', 'mode', m, d),
  auth: (m: string, d?: Record<string, unknown>) => emit('info', 'auth', m, d),
  select: (m: string, d?: Record<string, unknown>) => emit('info', 'select', m, d),
  mission: (m: string, d?: Record<string, unknown>) => emit('info', 'mission', m, d),

  // Level escalations — first arg is the category so the timeline stays
  // sortable.
  warn: (category: LogCategory, m: string, d?: Record<string, unknown>) =>
    emit('warn', category, m, d),
  error: (category: LogCategory, m: string, d?: Record<string, unknown>) =>
    emit('error', category, m, d),

  // Inspection / export.
  history: (): readonly LogEntry[] => ring,
  subscribe: (fn: (e: LogEntry) => void): (() => void) => {
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  },
  clear: () => {
    ring.length = 0;
  },
  /** Toggle console mirroring (defaults to on in dev). */
  setConsoleMirror: (on: boolean) => {
    consoleMirror = on;
  },
  /** Dump the whole buffer as pretty JSON. */
  toJSON: (): string => JSON.stringify(ring, null, 2),
  /** Trigger a browser file download of the current buffer. */
  download: () => {
    const blob = new Blob([JSON.stringify(ring, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fabricos-log-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};
